import React, { useState, useEffect } from 'react';
import {
    Container, Typography, TextField, Button, Snackbar, Alert,
    Paper, Grid, Box, FormControl, InputLabel, Select, MenuItem,
    IconButton, Divider, FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '../utils/errorHandler';

const AddProduct = () => {
  const [product, setProduct] = useState({
    productName: '',
    description: '',
    category: '',
    rateIncludesGST: false,
    preferredLocation: ''
  });
  const [locationStock, setLocationStock] = useState([]);
  const [categories, setCategories] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [isLoading, setIsLoading] = useState(false);
  const [similarProductsDialog, setSimilarProductsDialog] = useState({ open: false, products: [] });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
    }

    fetchInitialData();
  }, [navigate]);

  const fetchInitialData = async () => {
    try {
      const [categoriesResponse, locationsResponse] = await Promise.all([
        api.get('/categories'),
        api.get('/config/locations')
      ]);

      setCategories(categoriesResponse.data);
      const storageLocations = locationsResponse.data.storageLocations;
      setAvailableLocations(storageLocations);

      // Initialize with first location
      if (storageLocations.length > 0) {
        setLocationStock([{ location: storageLocations[0], stock: 0 }]);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to fetch initial data',
        severity: 'error'
      });
    }
  };

  const handleChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };

  const handleLocationStockChange = (index, field, value) => {
    const updated = [...locationStock];
    updated[index][field] = field === 'stock' ? parseFloat(value) || 0 : value;
    setLocationStock(updated);
  };

  const addLocationStock = () => {
    setLocationStock([...locationStock, { location: '', stock: 0 }]);
  };

  const removeLocationStock = (index) => {
    if (locationStock.length > 1) {
      setLocationStock(locationStock.filter((_, i) => i !== index));
    }
  };

  const validateForm = () => {
    if (!product.productName.trim()) {
      setSnackbar({ open: true, message: 'Product name is required', severity: 'error' });
      return false;
    }
    if (!product.category) {
      setSnackbar({ open: true, message: 'Category is required', severity: 'error' });
      return false;
    }

    // Validate location stocks
    for (let i = 0; i < locationStock.length; i++) {
      if (!locationStock[i].location.trim()) {
        setSnackbar({ open: true, message: `Location ${i + 1} name is required`, severity: 'error' });
        return false;
      }
      if (locationStock[i].stock < 0) {
        setSnackbar({ open: true, message: `Stock for location ${locationStock[i].location} cannot be negative`, severity: 'error' });
        return false;
      }
    }

    // Check for duplicate locations
    const locations = locationStock.map(ls => ls.location.trim().toLowerCase());
    const uniqueLocations = new Set(locations);
    if (locations.length !== uniqueLocations.size) {
      setSnackbar({ open: true, message: 'Duplicate locations are not allowed', severity: 'error' });
      return false;
    }

    return true;
  };

  const checkSimilarProducts = async () => {
    try {
      const response = await api.get(`/products/check-similar?productName=${encodeURIComponent(product.productName)}`);
      return response.data;
    } catch (error) {
      console.error('Error checking similar products:', error);
      return { hasSimilar: false, similarProducts: [] };
    }
  };

  const handleSubmit = async (e, skipCheck = false) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Check for similar products if not skipped
    if (!skipCheck) {
      setIsLoading(true);
      const similarCheck = await checkSimilarProducts();
      setIsLoading(false);

      if (similarCheck.hasSimilar) {
        setSimilarProductsDialog({ open: true, products: similarCheck.similarProducts });
        return;
      }
    }

    setIsLoading(true);
    try {
      const productData = {
        ...product,
        locationStock: locationStock.filter(ls => ls.location.trim() !== '')
      };
      const response = await api.post('/products', productData);
      console.log('Product added successfully:', response.data);
      setSnackbar({ open: true, message: 'Product added successfully', severity: 'success' });
      setProduct({ productName: '', description: '', category: '', rateIncludesGST: false, preferredLocation: '' });
      // Reset to first available location
      setLocationStock(availableLocations.length > 0 ? [{ location: availableLocations[0], stock: 0 }] : []);
      setSimilarProductsDialog({ open: false, products: [] });
    } catch (error) {
      handleApiError(error, setSnackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleProceedWithCreation = (e) => {
    handleSubmit(e, true); // Skip similar product check
  };

  return (
        <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 1
                    }}
                >
                    Add New Product
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Fill in the details to add a product to your inventory
                </Typography>
            </Box>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="productName"
                            label="Product Name"
                            value={product.productName}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            autoFocus
                        />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth required>
                        <InputLabel>Category</InputLabel>
                        <Select
                          name="category"
                          value={product.category}
                          onChange={handleChange}
                          label="Category"
                        >
                          {categories.map((category) => (
                            <MenuItem key={category._id} value={category._id}>
                              {category.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            name="description"
                            label="Description"
                            value={product.description}
                            onChange={handleChange}
                            multiline
                            rows={4}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={product.rateIncludesGST}
                                    onChange={(e) => setProduct({ ...product, rateIncludesGST: e.target.checked })}
                                    name="rateIncludesGST"
                                />
                            }
                            label="Product rates include GST"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel>Preferred Location</InputLabel>
                            <Select
                                name="preferredLocation"
                                value={product.preferredLocation}
                                onChange={handleChange}
                                label="Preferred Location"
                            >
                                <MenuItem value="">
                                    <em>No Preference</em>
                                </MenuItem>
                                {availableLocations.map((loc) => (
                                    <MenuItem key={loc} value={loc}>
                                        {loc}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                            Orders will preferably use this location if sufficient stock is available
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Divider sx={{ my: 2 }}>
                            <Typography variant="overline" color="text.secondary">Stock Information</Typography>
                        </Divider>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" color="primary.main">Location Stock</Typography>
                            <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={addLocationStock}
                                size="small"
                                sx={{ textTransform: 'none' }}
                            >
                                Add Location
                            </Button>
                        </Box>
                    </Grid>
                    {locationStock.map((ls, index) => (
                        <React.Fragment key={index}>
                            <Grid item xs={5}>
                                <FormControl fullWidth required>
                                    <InputLabel>{`Location ${index + 1}`}</InputLabel>
                                    <Select
                                        value={ls.location}
                                        onChange={(e) => handleLocationStockChange(index, 'location', e.target.value)}
                                        label={`Location ${index + 1}`}
                                    >
                                        {availableLocations.map((loc) => (
                                            <MenuItem key={loc} value={loc}>
                                                {loc}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={5}>
                                <TextField
                                    fullWidth
                                    label="Stock"
                                    type="number"
                                    value={ls.stock}
                                    onChange={(e) => handleLocationStockChange(index, 'stock', e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    variant="outlined"
                                    required
                                    inputProps={{ min: 0, step: 0.01 }}
                                    placeholder="0"
                                />
                            </Grid>
                            <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                    onClick={() => removeLocationStock(index)}
                                    disabled={locationStock.length === 1}
                                    color="error"
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Grid>
                        </React.Fragment>
                    ))}
                </Grid>
                <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{
                      px: 4,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      }
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Adding Product...' : 'Add Product'}
                  </Button>
                </Box>
            </form>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Similar Products Warning Dialog */}
            <Dialog
                open={similarProductsDialog.open}
                onClose={() => setSimilarProductsDialog({ open: false, products: [] })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                    ⚠️ Similar Products Found
                </DialogTitle>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="body1" gutterBottom>
                        The product name "{product.productName}" is similar to existing products:
                    </Typography>
                    <List>
                        {similarProductsDialog.products.map((similar, index) => (
                            <ListItem key={index}>
                                <ListItemText
                                    primary={similar.productName}
                                    secondary={`${similar.similarity}% similar`}
                                />
                            </ListItem>
                        ))}
                    </List>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Are you sure you want to proceed with creating this product?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setSimilarProductsDialog({ open: false, products: [] })}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleProceedWithCreation}
                        variant="contained"
                        color="warning"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Creating...' : 'Create Anyway'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    </Container>
  );
};

export default AddProduct;